<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVModel } from '@vueuse/core'

import BaseMultiSelect from '@/components/base/BaseMultiSelect.vue'
import BaseChip from '@/components/base/BaseChip.vue'
import type { Option } from '@/components/base/BaseMultiSelect.vue'
import useCountries from '@/composables/countries'

import type { AudienceRule, Country } from '@/types/types'
import type { HighlightInfoFn } from '../composables/rulesMatcher'

// 27 EU member states only
const EUROPEAN_ALPHA2 = new Set([
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK'
])

const UNKNOWN_SENTINEL = '__unknown__'

const { t } = useI18n()
const { user } = useLocalStorage()

const props = defineProps<{
  rules: Record<string, AudienceRule>
  classes: any
  disabled: boolean
  getHighlightInfo: HighlightInfoFn
}>()

const emit = defineEmits(['update:rules'])
const rules = useVModel(props, 'rules', emit)

if (!rules.value.country) {
  rules.value.country = { value: [], operator: 'in' }
}

const { countries, isLoadingCountries, countriesError, fetchCountries } = useCountries()

const flagByAlpha2Code = computed<Map<string, string>>(
  () =>
    new Map(
      countries.value
        .filter((c) => c.alpha2Code && c.flag)
        .map((c) => [String(c.alpha2Code).toUpperCase(), c.flag as string])
    )
)

const OPERATOR_IN = 1
const OPERATOR_NOT_IN = 2

const operatorOptions = computed(() => [
  { id: OPERATOR_IN, text: t('pages.audience.operators.in') },
  { id: OPERATOR_NOT_IN, text: t('pages.audience.operators.ni') }
])

const selectedOperatorId = computed<number>({
  get: () => (rules.value.country?.operator === 'ni' ? OPERATOR_NOT_IN : OPERATOR_IN),
  set: (id: number) => {
    if (!rules.value.country) return
    rules.value.country = { ...rules.value.country, operator: id === OPERATOR_NOT_IN ? 'ni' : 'in' }
  }
})

const updateOperator = (id: number) => {
  selectedOperatorId.value = id
}

const merchantCountryId = computed<number | null>(() => user.value?.countryId ?? null)

const sortedCountryOptions = computed<Option[]>(() => {
  if (countries.value.length === 0) return []

  const merchantId = merchantCountryId.value
  const toOption = (c: Country): Option => ({
    value: String(c.alpha2Code).toUpperCase(),
    label: c.name ?? c.alpha2Code ?? String(c.id)
  })

  const merchantCountry = merchantId != null ? countries.value.find((c) => c.id === merchantId && c.alpha2Code) : null
  const europeanCountries = countries.value
    .filter((c) => c.alpha2Code && EUROPEAN_ALPHA2.has(c.alpha2Code) && c.id !== merchantId)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
  const otherCountries = countries.value
    .filter((c) => c.alpha2Code && !EUROPEAN_ALPHA2.has(c.alpha2Code) && c.id !== merchantId)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))

  return [
    ...(merchantCountry ? [toOption(merchantCountry)] : []),
    ...europeanCountries.map(toOption),
    ...otherCountries.map(toOption)
  ]
})

const countryOptions = computed<Option[]>(() => [
  { value: UNKNOWN_SENTINEL, label: t('pages.audience.audienceCreatePage.block.profil.country.unknown') },
  ...sortedCountryOptions.value
])

const selectedValues = computed<string[]>({
  get: () => {
    const value = rules.value.country?.value
    if (!Array.isArray(value)) return []
    return value
      .map((v) => {
        if (v === null) {
          return UNKNOWN_SENTINEL
        }
        return String(v).toUpperCase()
      })
      .filter((v) => typeof v === 'string')
  },
  set: (values: string[]) => {
    if (!rules.value.country) return
    rules.value.country = {
      ...rules.value.country,
      value: values.map((value) => (value === UNKNOWN_SENTINEL ? null : String(value).toUpperCase()))
    }
  }
})

const countryHighlight = computed(() => props.getHighlightInfo('country'))
const retryFetchCountries = () => fetchCountries(true)
const onOpenCountries = () => fetchCountries()

onMounted(() => {
  if (selectedValues.value.length > 0) {
    fetchCountries()
  }
})
</script>

<template>
  <div
    :class="[
      classes.rowGrid,
      {
        'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600': countryHighlight.hasDefined
      }
    ]"
  >
    <label :class="[classes.label, 'flex items-center gap-1']">
      <span>
        {{ t('pages.audience.audienceCreatePage.block.profil.country.label') }}
      </span>
      <Tooltip>
        <TooltipTrigger as-child>
          <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
        </TooltipTrigger>
        <TooltipContent>
          {{ t('pages.audience.audienceCreatePage.block.profil.country.tooltip') }}
        </TooltipContent>
      </Tooltip>
    </label>
    <div :class="classes.inputs">
      <ComboboxSelect
        value-key="id"
        label-key="text"
        :disabled="disabled"
        :search-threshold="Infinity"
        :model-value="selectedOperatorId"
        :options="operatorOptions"
        trigger-class="w-full"
        @update:model-value="updateOperator"
      />
      <BaseMultiSelect
        v-model="selectedValues"
        class="min-w-0"
        :disabled="disabled"
        :loading="isLoadingCountries"
        :options="countryOptions"
        :max-selected-labels="2"
        fluid
        filter
        :filter-match-mode="'contains'"
        :filter-placeholder="t('global.multiselect.default.filterPlaceholder')"
        :empty-filter-message="t('global.multiselect.default.emptyFilterMessage')"
        :empty-message="countriesError ? t('notifications.save.error') : t('global.multiselect.default.noResult')"
        :show-toggle-all="false"
        :aria-label="t('pages.audience.audienceCreatePage.block.profil.country.label')"
        :placeholder="t('pages.audience.audienceCreatePage.block.profil.presence.placeholder')"
        @show="onOpenCountries"
      >
        <template #header>
          <div v-if="isLoadingCountries" class="flex items-center gap-2 p-2 text-sm text-muted-foreground">
            <div class="h-4 w-4 animate-spin rounded-full border-2 border-border border-l-transparent" />
            <span>{{ t('global.words.loading') }}</span>
          </div>
        </template>
        <template #empty>
          <div class="space-y-2 p-3 text-center text-muted-foreground">
            <p>
              {{ countriesError ? t('notifications.save.error') : t('global.multiselect.default.emptyFilterMessage') }}
            </p>
            <button
              v-if="countriesError"
              type="button"
              class="text-sm font-medium text-primary hover:underline"
              @click="retryFetchCountries"
            >
              {{ t('global.words.refresh') }}
            </button>
          </div>
        </template>
        <template #option="{ option }">
          <div class="flex items-center gap-2">
            <img
              v-if="flagByAlpha2Code.get(String(option.value))"
              :src="flagByAlpha2Code.get(String(option.value))"
              :alt="option.label"
              class="h-3.5 w-5 shrink-0 object-cover"
            />
            <span>{{ option.label }}</span>
          </div>
        </template>
        <template #chip="{ option, remove }">
          <BaseChip :text="option.label" @click="remove">
            <template #label>
              <img
                v-if="flagByAlpha2Code.get(String(option.value))"
                :src="flagByAlpha2Code.get(String(option.value))"
                :alt="option.label"
                class="h-3 w-4 shrink-0 object-cover"
              />
              {{ option.label }}
            </template>
          </BaseChip>
        </template>
      </BaseMultiSelect>
    </div>
  </div>
</template>
